import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Form, Button, Row, Col, Alert, Card, InputGroup } from 'react-bootstrap';
import { faSignInAlt, faEye, faEyeSlash, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import brainlinkLogo from '../../assets/images/brainlink.png';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Login = ({ onLogin }) => {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loginError, setLoginError] = useState('');
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear error when user types
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Password is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        setLoginError('');

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/login`, {
                email: formData.email,
                password: formData.password
            });

            // Extract user data from response
            const userData = {
                id: response.data.id,
                name: response.data.name,
                email: response.data.email,
                role: response.data.role
            };

            onLogin(userData, response.data.access_token); // Call the parent component's login handler

            // Reset form
            setFormData({
                email: '',
                password: ''
            });

            navigate('/'); // Navigate to home

        } catch (err) {
            let errorMessage = 'Login failed. Please try again.';
            if (err.response) {
                if (err.response.status === 401) {
                    errorMessage = err.response.data.message || 'Invalid email or password';
                } else if (err.response.status === 400) {
                    errorMessage = 'Invalid request. Please check your inputs.';
                }
            } else if (err.request) {
                errorMessage = 'Network error. Please check your connection.';
            }

            setLoginError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-4 p-md-5">
                        <div className="text-center mb-4">
                            <img
                                src={brainlinkLogo}
                                alt="BrainLink Health Logo"
                                style={{
                                    height: '270px',
                                    width: 'auto',
                                    marginBottom: '3rem'
                                }}
                            />
                            </div>
                        <h3 className="mt-3 fw-bold text-center">SIGN IN</h3>


                        {loginError && (
                            <Alert
                                variant="danger"
                                onClose={() => setLoginError('')}
                                dismissible
                                className="mb-4"
                            >
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                {loginError}
                            </Alert>
                        )}

                        <Form onSubmit={handleSubmit} noValidate>
                            <Form.Group className="mb-3" controlId="email">
                                <Form.Label>E-mail Address</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    isInvalid={!!errors.email}
                                    placeholder="Enter your e-mail"
                                    autoComplete="username"
                                />
                                <Form.Control.Feedback type="invalid">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                    {errors.email}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-4" controlId="password">
                                <Form.Label>Password</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        isInvalid={!!errors.password}
                                        placeholder="Enter your password"
                                        autoComplete="current-password"
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        disabled={isSubmitting}
                                    >
                                        <FontAwesomeIcon
                                            icon={showPassword ? faEyeSlash : faEye}
                                            className={showPassword ? "text-dark" : "text-secondary"}
                                        />
                                    </Button>
                                </InputGroup>
                                <Form.Control.Feedback type="invalid" className="d-block">
                                    {errors.password && (
                                        <>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                            {errors.password}
                                        </>
                                    )}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Button
                                variant="primary"
                                type="submit"
                                className="w-100 mb-3"
                                disabled={isSubmitting}
                                size="lg"
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Signing in...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSignInAlt} className="me-2" /> Sign In
                                    </>
                                )}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default Login;